import { isNil } from 'lodash';
import { ReactNode, useMemo, useState } from 'react';
import { View } from '@shared/components/View';
import { MBIconV2, MBIconV2Name } from '@shared/components/image';
import { MBSearchBar } from '@shared/components/searchBar';
import { Colors, bounceIn, grid } from '@shared/styles';
import { MenuItem } from './MenuItem';
import { MenuScrollable } from './MenuScrollable';
import { MenuSection } from './MenuSection';

/**
 * Menu.Options / Menu.SearchableOptions
 *
 * Renders a generic list of selectable `Menu.Item` rows from an array of items.
 * Selected items are marked with a checkmark via `isItemSelected`.
 *
 * `Menu.SearchableOptions` wraps `Menu.Options` with a search bar that filters
 * items client-side by label. Use it when the option list is long enough that
 * users benefit from filtering.
 */

const SEARCH_THRESHOLD = 10;

interface MenuOptionsProps<T> {
  options: T[];
  getItemKey: (item: T) => string;
  getItemLabel: (item: T) => string;
  getItemIcon?: (item: T) => MBIconV2Name | undefined | 'none';
  onSelect?: (item: T) => void;
  isItemSelected?: (item: T) => boolean;
  isItemDisabled?: (item: T) => boolean;
  renderItem?: (item: T) => ReactNode;
}

export function MenuOptions<T>({
  options,
  getItemKey,
  onSelect,
  isItemSelected,
  getItemLabel,
  getItemIcon,
  isItemDisabled,
  renderItem,
}: MenuOptionsProps<T>) {
  return (
    <MenuScrollable showScrollIndicators={options.length > SEARCH_THRESHOLD}>
      {isNil(renderItem) &&
        options.map(option => (
          <MenuItem
            key={getItemKey?.(option)}
            icon={getItemIcon?.(option)}
            label={getItemLabel(option)}
            onClick={() => onSelect?.(option)}
            disabled={isItemDisabled?.(option)}
            endContent={<SelectedMarker isSelected={isItemSelected?.(option) ?? false} />}
          />
        ))}
      {!isNil(renderItem) && options.map(option => renderItem(option))}
    </MenuScrollable>
  );
}

export function SelectedMarker({ isSelected }: { isSelected: boolean }) {
  const key = isSelected ? 'selected' : 'not-selected';
  return (
    <View center style={{ width: grid(3) }} key={key} className={isSelected && bounceIn}>
      {isSelected && <MBIconV2 color={Colors.v2.green} name="check-box" size={grid(3.5)} />}
    </View>
  );
}

export function CheckboxMarker({ isSelected }: { isSelected: boolean }) {
  const key = isSelected ? 'selected' : 'not-selected';
  const icon = isSelected ? 'check-box' : 'check-box-empty';
  return (
    <View center style={{ width: grid(3) }} key={key} className={isSelected && bounceIn}>
      <MBIconV2 color={Colors.v2.green} name={icon} size={grid(3.5)} />
    </View>
  );
}

interface SearchableOptionsProps<T> extends MenuOptionsProps<T> {
  title?: string;
}

export function SearchableOptions<T>({
  title,
  options,
  onSelect,
  isItemSelected,
  getItemKey,
  getItemLabel,
  getItemIcon,
  isItemDisabled,
  renderItem,
}: SearchableOptionsProps<T>) {
  const [filter, setFilter] = useState('');

  const filteredOptions = useMemo(() => {
    return options.filter(option => {
      const label = getItemLabel(option);
      return label.toLowerCase().includes(filter.toLowerCase());
    });
  }, [filter, options, getItemLabel]);

  const showSearch = options.length > SEARCH_THRESHOLD;
  const showHeader = !isNil(title) || showSearch;

  return (
    <>
      {showHeader && (
        <MenuSection title={title}>
          {showSearch && <MBSearchBar value={filter} onChangeText={setFilter} />}
        </MenuSection>
      )}
      <MenuOptions
        options={filteredOptions}
        onSelect={onSelect}
        isItemSelected={isItemSelected}
        getItemKey={getItemKey}
        getItemLabel={getItemLabel}
        getItemIcon={getItemIcon}
        isItemDisabled={isItemDisabled}
        renderItem={renderItem}
      />
    </>
  );
}
